import urljoin from 'url-join';
import useQuery from 'hooks/useQuery';

import {useHistory, useLocation} from 'react-router';
import {alertByError} from 'utils/alerts';
import {
  postSubscribe,
  postSubscribeAddon,
  postSubscribeTrial,
  postSubscribeTrialToBasic,
} from 'api/subscriptions';
import {useMembership} from './MembershipContext';
import {generateUtmLink} from 'components/Links/UtmLink';
import {createContext, useContext, useState} from 'react';

import {IAF_MAIN_URL} from 'configs/Environment';
import {
  SUBSCRIPTION_BILLING_METHOD,
  SUBSCRIPTION_PLANS_LEVEL_TYPE,
  SUBSCRIPTION_TYPE,
} from 'configs/membership';
import {buildUrlParams} from 'utils/string';
import {useAuth} from './AuthContext';
import {isEmpty, isEqual} from 'lodash';
import CheckoutPaymentFailedModal from 'modules/membership/plans/checkout/CheckoutPaymentFailedModal';
import cleanDeep from 'clean-deep';
import urlJoin from 'url-join';
import {
  getClientId,
  getGCLIdAdWords,
  getGCLIdAutoTaggingUniversal,
} from 'utils/google-cookies';

const SubscriptionContext = createContext({});

function SubscriptionProvider(props) {
  const {children} = props;
  const {query} = useQuery();
  const history = useHistory();
  const {pathname} = useLocation();
  const {subscriptionLevel, isLegacyUser} = useMembership();
  const {token, handleLoginBySession} = useAuth();

  const {redirect, plan, market_intelligence, source, rerun} = query || {};
  const {utm_source, utm_medium, utm_content, utm_campaign} = query || {};

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);

  const togglePaymentFailedModal = () => setIsPaymentFailed((prev) => !prev);

  const isSubscribingEnterprise = isEqual(plan, SUBSCRIPTION_TYPE.enterprise);
  const cleanedQuery = new URLSearchParams(query);
  cleanedQuery.delete('cancelled');

  async function onSubscribe(
    planId,
    plan,
    accounts,
    additionalSeats,
    isBlocked,
    paymentMethod,
    addOns
  ) {
    const successUrlParams = {
      plan,
      rerun,
      success: true,
      redirect: redirect || 'dashboard',
      previous: SUBSCRIPTION_PLANS_LEVEL_TYPE[subscriptionLevel],
      current: plan,
    };

    const utmValues = {
      path: pathname,
      source: utm_source,
      medium: utm_medium,
      content: utm_content,
      campaign: utm_campaign,
      params: successUrlParams,
    };

    const utmParams = generateUtmLink(utmValues);

    const url = {
      success: buildUrlParams(
        urljoin(IAF_MAIN_URL, utmParams),
        cleanDeep({source})
      ),
      cancel: buildUrlParams(
        urljoin(IAF_MAIN_URL, pathname),
        cleanDeep({
          ...query,
          plan,
          redirect,
          cancel: true,
          market_intelligence,
          ...(isBlocked && isSubscribingEnterprise && {cancelled: true}),
        })
      ),
    };

    setIsSubscribing(true);
    try {
      const payload = {
        account_ids: accounts,
        subscription_plans_type: plan,
        subscription_plans_id: planId,
        url,
        additional_seats: parseInt(additionalSeats || 0),
        billing_method: paymentMethod || SUBSCRIPTION_BILLING_METHOD.stripe,
        addons: addOns,
      };

      const additionalTrackingPayload = cleanDeep({
        client_id: getClientId(),
        gcl_aw: getGCLIdAdWords(),
        gcl_au: getGCLIdAutoTaggingUniversal(),
      });

      const {success_url} =
        plan === 'free_trial'
          ? await postSubscribeTrial({
              ...payload,
              ...additionalTrackingPayload,
            })
          : await postSubscribe(payload);

      if (success_url) {
        window.location.href = success_url;
      }
    } catch (e) {
      const {code} = e || {};
      if (code === 'incomplete_payment' || code === 'card_was_declined') {
        togglePaymentFailedModal();
      } else {
        alertByError(e);
      }
    } finally {
      setIsSubscribing(false);
    }
  }

  async function upgradeToBasic(payload) {
    setIsSubscribing(true);

    const url = buildUrlParams('/membership/plans/checkout', {
      success: true,
      redirect: redirect || 'dashboard',
      previous: 'free_trial',
      current: SUBSCRIPTION_TYPE.basic,
    });

    try {
      const {data} = await postSubscribeTrialToBasic(!isLegacyUser && payload);

      if (data) {
        handleContinue(url);
      }
    } catch (e) {
      const {code} = e || {};

      if (code === 'incomplete_payment' || code === 'card_was_declined') {
        togglePaymentFailedModal();
      } else {
        alertByError(e);
      }
    } finally {
      setIsSubscribing(false);
    }
  }

  async function onAddon(addons, additionalSeats) {
    setIsSubscribing(true);

    try {
      await postSubscribeAddon({
        addons,
        additional_seats: additionalSeats || 0,
      });

      window.location.replace(
        urlJoin(
          IAF_MAIN_URL,
          buildUrlParams('/membership/plans/checkout', {
            success: true,
            redirect: '/analytics/certifications',
          })
        )
      );
    } catch (e) {
      const {code} = e || {};
      if (code === 'incomplete_payment' || code === 'card_was_declined') {
        togglePaymentFailedModal();
      } else {
        alertByError(e);
      }
    } finally {
      setIsSubscribing(true);
    }
  }

  async function onViewInvoice({planId, additionalSeats, isBlocked}) {
    setIsSubscribing(true);
    const successUrlParams = {
      plan,
      success: true,
      redirect: redirect || 'dashboard',
      previous: SUBSCRIPTION_PLANS_LEVEL_TYPE[subscriptionLevel],
      current: plan,
    };

    const utmValues = {
      path: pathname,
      source: utm_source,
      medium: utm_medium,
      content: utm_content,
      campaign: utm_campaign,
      params: successUrlParams,
    };

    const utmParams = generateUtmLink(utmValues);

    const url = {
      success: urljoin(IAF_MAIN_URL, utmParams),
      cancel: buildUrlParams(
        urljoin(IAF_MAIN_URL, pathname),
        cleanDeep({
          plan,
          redirect,
          cancel: true,
          ...(isBlocked && isSubscribingEnterprise && {cancelled: true}),
        })
      ),
    };
    try {
      const payload = {
        subscription_plans_id: planId,
        billing_method: 'stripe_bank_transfer',
        additional_seats: parseInt(additionalSeats || 0),
        url,
      };

      const {success_url} = await postSubscribe(payload);

      if (success_url) {
        window.location.replace(success_url);
      }
    } catch (e) {
      alertByError(e);
    } finally {
      setIsSubscribing(false);
    }
  }

  async function handleContinue(url) {
    const data = await handleLoginBySession(token);

    if (!isEmpty(data)) {
      history.push(url);
    }
  }

  return (
    <SubscriptionContext.Provider
      value={{
        onAddon,
        onSubscribe,
        onViewInvoice,
        handleContinue,
        upgradeToBasic,
        isSubscribing,
      }}
    >
      {children}

      <CheckoutPaymentFailedModal
        isOpen={isPaymentFailed}
        toggle={togglePaymentFailedModal}
      />
    </SubscriptionContext.Provider>
  );
}

const useSubscription = () => useContext(SubscriptionContext);

export {useSubscription, SubscriptionContext, SubscriptionProvider};
